'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { profilesApi } from '@/lib/api/profiles';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslations } from '@/i18n/useTranslations';

export function ProfileReviewAlert() {
  const user = useAuthStore((state) => state.user);
  const [dismissed, setDismissed] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const t = useTranslations('profiles');
  const tCommon = useTranslations('common');

  const { data: warnings } = useQuery({
    queryKey: ['profileWarnings'],
    queryFn: () => profilesApi.getMyWarnings(),
    enabled: !!user,
  });

  if (!warnings?.length || dismissed) return null;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && setDismissed(true)}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning-text" />
            {t('reviewAlert.title')}
          </DialogTitle>
          <DialogDescription>
            {t('reviewAlert.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {warnings.map((warning) => (
            <div
              key={warning.profileId}
              className="rounded-xl border border-surface bg-overlay/50 p-4"
            >
              <button
                className="flex w-full items-center justify-between"
                onClick={() =>
                  setExpandedId(expandedId === warning.profileId ? null : warning.profileId)
                }
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{warning.profileName}</span>
                  <Badge variant="destructive" className="text-xs">
                    {t('status.REJECTED')}
                  </Badge>
                </div>
                {expandedId === warning.profileId ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {expandedId === warning.profileId && (
                <div className="mt-3 space-y-2">
                  <div className="text-sm text-muted-foreground">
                    {t('reviewAlert.issues')}:
                  </div>
                  <ul className="space-y-1">
                    {warning.issues.map((issue, idx) => (
                      <li key={idx} className="text-sm">
                        - {issue.label}: {issue.reason}
                      </li>
                    ))}
                  </ul>
                  <div className="text-sm text-muted-foreground">
                    {t('reviewAlert.enrolled')}: {warning.enrolledCompetitions.join(', ')}
                  </div>
                  <Link to={`/dashboard/profiles/${warning.profileId}`}>
                    <Button size="sm" className="mt-2">
                      {t('reviewAlert.fix')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDismissed(true)}>
            {t('reviewAlert.later')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
